import {
  customElement,
  DeesElement,
  html,
  type TemplateResult,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { syntaxPaletteStyles, syntaxScopeStyles } from '../../00syntax.js';
import { demoFunc } from './dees-workspace-markdownoutlet.demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-workspace-markdownoutlet': DeesWorkspaceMarkdownoutlet;
  }
}

@customElement('dees-workspace-markdownoutlet')
export class DeesWorkspaceMarkdownoutlet extends DeesElement {
  // DEMO
  public static demo = demoFunc;
  public static demoGroups = ['Workspace'];

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        /* callout accent (dark default, bright override below) */
        --mdoutlet-purple: #a371f7;
        display: block;
        font-family: var(--dees-font-family);
        font-size: 16px;
        line-height: 1.6;
        color: var(--dees-color-text-primary);
      }

      :host([gobright]) {
        --mdoutlet-purple: #8250df;
      }

      .outlet {
        word-wrap: break-word;
      }

      /* Headings */
      h1, h2, h3, h4, h5, h6 {
        margin-top: 24px;
        margin-bottom: 16px;
        font-weight: 600;
        line-height: 1.25;
        color: var(--dees-color-text-primary);
      }

      h1 {
        font-size: 2em;
        padding-bottom: 0.3em;
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      h2 {
        font-size: 1.5em;
        padding-bottom: 0.3em;
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      h3 {
        font-size: 1.25em;
      }

      h4 {
        font-size: 1em;
      }

      h5 {
        font-size: 0.875em;
      }

      h6 {
        font-size: 0.85em;
        color: var(--dees-color-text-secondary);
      }

      /* Paragraphs and text */
      p {
        margin-top: 0;
        margin-bottom: 16px;
      }

      /* Links */
      a {
        color: var(--dees-color-link);
        text-decoration: none;
      }

      a:hover {
        text-decoration: underline;
      }

      /* Strong and emphasis */
      strong {
        font-weight: 600;
        color: var(--dees-color-text-primary);
      }

      em {
        font-style: italic;
      }

      /* Lists */
      ul, ol {
        margin-top: 0;
        margin-bottom: 16px;
        padding-left: 2em;
      }

      li {
        margin-top: 4px;
      }

      li + li {
        margin-top: 4px;
      }

      ul ul, ul ol, ol ul, ol ol {
        margin-top: 0;
        margin-bottom: 0;
      }

      /* Blockquotes */
      blockquote {
        margin: 0 0 16px 0;
        padding: 0 1em;
        color: var(--dees-color-text-muted);
        border-left: 4px solid var(--dees-color-border-default);
      }

      blockquote > :first-child {
        margin-top: 0;
      }

      blockquote > :last-child {
        margin-bottom: 0;
      }

      /* Inline code */
      code {
        padding: 0.2em 0.4em;
        margin: 0;
        font-size: 85%;
        font-family: ui-monospace, Menlo, Consolas, 'Liberation Mono', monospace;
        background-color: var(--dees-color-code-bg);
        border-radius: 6px;
        white-space: break-spaces;
      }

      /* Code blocks */
      pre {
        margin-top: 0;
        margin-bottom: 16px;
        padding: 16px;
        overflow: auto;
        font-size: 85%;
        line-height: 1.45;
        font-family: ui-monospace, Menlo, Consolas, 'Liberation Mono', monospace;
        background-color: var(--dees-color-code-block-bg);
        border-radius: 6px;
        word-wrap: normal;
      }

      pre code {
        display: block;
        padding: 0;
        margin: 0;
        overflow: visible;
        line-height: inherit;
        word-wrap: normal;
        background-color: transparent;
        border: 0;
        font-size: 100%;
        white-space: pre;
      }

      /* Tables */
      table {
        width: max-content;
        max-width: 100%;
        margin-top: 0;
        margin-bottom: 16px;
        border-spacing: 0;
        border-collapse: collapse;
        overflow: auto;
        display: block;
      }

      table th {
        font-weight: 600;
        padding: 6px 13px;
        border: 1px solid var(--dees-color-border-default);
        background-color: var(--dees-color-code-block-bg);
        color: var(--dees-color-text-primary);
      }

      table td {
        padding: 6px 13px;
        border: 1px solid var(--dees-color-border-default);
        color: var(--dees-color-text-primary);
      }

      table tr {
        background-color: var(--dees-color-bg-primary);
        border-top: 1px solid var(--dees-color-border-default);
      }

      table tr:nth-child(2n) {
        background-color: var(--dees-color-code-block-bg);
      }

      /* Horizontal rules */
      hr {
        height: 4px;
        padding: 0;
        margin: 24px 0;
        background-color: var(--dees-color-border-default);
        border: 0;
        border-radius: 2px;
      }

      /* Images */
      img {
        max-width: 100%;
        box-sizing: border-box;
        border-radius: 6px;
      }

      /* Task lists */
      .task-list-item {
        list-style-type: none;
      }

      .task-list-item input {
        margin: 0 0.2em 0.25em -1.4em;
        vertical-align: middle;
      }

      /* Definition lists */
      dl {
        padding: 0;
      }

      dl dt {
        padding: 0;
        margin-top: 16px;
        font-size: 1em;
        font-style: italic;
        font-weight: 600;
      }

      dl dd {
        padding: 0 16px;
        margin-bottom: 16px;
      }

      /* Keyboard input */
      kbd {
        display: inline-block;
        padding: 3px 5px;
        font-size: 11px;
        font-family: ui-monospace, Menlo, Consolas, 'Liberation Mono', monospace;
        line-height: 10px;
        color: var(--dees-color-text-primary);
        vertical-align: middle;
        background-color: var(--dees-color-code-block-bg);
        border: 1px solid var(--dees-color-border-default);
        border-radius: 6px;
        box-shadow: inset 0 -1px 0 var(--dees-color-border-default);
      }

      /* Footnotes */
      .footnotes {
        font-size: 12px;
        color: var(--dees-color-text-muted);
        border-top: 1px solid var(--dees-color-border-default);
        padding-top: 16px;
        margin-top: 32px;
      }

      /* Alerts/Admonitions */
      .markdown-alert {
        padding: 8px 16px;
        margin-bottom: 16px;
        border-left: 4px solid;
        border-radius: 6px;
      }

      .markdown-alert-note {
        border-color: var(--dees-color-link);
        background-color: color-mix(in srgb, var(--dees-color-link) 10%, transparent);
      }

      .markdown-alert-warning {
        border-color: var(--dees-color-text-warning);
        background-color: color-mix(in srgb, var(--dees-color-text-warning) 10%, transparent);
      }

      .markdown-alert-important {
        border-color: var(--mdoutlet-purple);
        background-color: color-mix(in srgb, var(--mdoutlet-purple) 10%, transparent);
      }

      .markdown-alert-caution {
        border-color: var(--dees-color-text-error);
        background-color: color-mix(in srgb, var(--dees-color-text-error) 10%, transparent);
      }

      .markdown-alert-tip {
        border-color: var(--dees-color-text-success);
        background-color: color-mix(in srgb, var(--dees-color-text-success) 10%, transparent);
      }

      .hljs-formula {
        background-color: var(--dees-color-code-block-bg);
      }
    `,
    /* Shared system syntax palette + highlight.js scope mapping */
    syntaxPaletteStyles,
    syntaxScopeStyles,
  ];

  // INSTANCE
  private outlet!: HTMLElement;

  public render(): TemplateResult {
    return html`
      <div class="outlet">
        <h1>Markdown Preview</h1>
        <p>Content will appear here when markdown is parsed.</p>
      </div>
    `;
  }

  public async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    await super.firstUpdated(_changedProperties);
    this.outlet = this.shadowRoot!.querySelector('.outlet')!;
  }

  public async updateHtmlText(htmlTextArg: string) {
    await this.updateComplete;
    if (!this.outlet) {
      this.outlet = this.shadowRoot!.querySelector('.outlet')!;
    }
    this.outlet.innerHTML = htmlTextArg;
  }
}
